import { useEffect, useRef, useState } from "react";
import api, { formatError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { useAdmin } from "@/contexts/AdminContext";
import { useBranding } from "@/contexts/BrandingContext";
import { Lock, Type, Upload, X, Save, Loader2, RefreshCw } from "lucide-react";

/**
 * /admin/branding — Admin can swap the site font, upload a logo, and
 * change the wordmark text. Lives behind the Back Office PIN.
 */
const FONT_OPTIONS = [
  { key: "system",         name: "System UI",         sample: "Aa" },
  { key: "inter",          name: "Inter",             sample: "Aa" },
  { key: "playfair",       name: "Playfair Display",  sample: "Aa" },
  { key: "space-grotesk",  name: "Space Grotesk",     sample: "Aa" },
  { key: "manrope",        name: "Manrope",           sample: "Aa" },
  { key: "dm-serif",       name: "DM Serif Display",  sample: "Aa" },
  { key: "ibm-plex",       name: "IBM Plex Sans",     sample: "Aa" },
  { key: "jetbrains-mono", name: "JetBrains Mono",    sample: "Aa" },
];

export default function AdminBranding() {
  const { unlocked } = useAdmin();
  const { branding, logoSrc, refresh } = useBranding();
  const [wordmark, setWordmark] = useState(branding?.wordmark || "Born Day");
  const [showWordmark, setShowWordmark] = useState(branding?.show_wordmark ?? true);
  const [tagline, setTagline] = useState(branding?.tagline || "Your calendar");
  const [slogan, setSlogan] = useState(branding?.slogan || "Every day. Every person. All in one place.");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    setWordmark(branding?.wordmark || "Born Day");
    setShowWordmark(branding?.show_wordmark ?? true);
    setTagline(branding?.tagline || "Your calendar");
    setSlogan(branding?.slogan || "Every day. Every person. All in one place.");
  }, [branding]);

  if (!unlocked) {
    return (
      <AppShell>
        <div className="bd-surface p-6 max-w-lg" data-testid="admin-branding-locked">
          <div className="flex items-center gap-2 font-bold text-lg mb-2">
            <Lock size={16} /> Locked
          </div>
          <p style={{ color: "var(--text-dim)" }}>
            Tap the <strong>Admin</strong> pill at the bottom-left and enter the passcode.
          </p>
        </div>
      </AppShell>
    );
  }

  const flash = (msg) => { setOk(msg); setTimeout(() => setOk(""), 2500); };

  const saveFont = async (font_key) => {
    setBusy(true); setErr("");
    try {
      await api.put("/admin/branding", { font_key });
      await refresh();
      flash(`Font set to ${font_key}.`);
    } catch (e) { setErr(formatError(e)); }
    finally { setBusy(false); }
  };

  const saveWordmark = async () => {
    setBusy(true); setErr("");
    try {
      await api.put("/admin/branding", { wordmark, show_wordmark: showWordmark });
      await refresh();
      flash("Wordmark saved.");
    } catch (e) { setErr(formatError(e)); }
    finally { setBusy(false); }
  };

  const saveHero = async () => {
    setBusy(true); setErr("");
    try {
      await api.put("/admin/branding", { tagline, slogan });
      await refresh();
      flash("Front-page slogan saved.");
    } catch (e) { setErr(formatError(e)); }
    finally { setBusy(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/branding/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      flash("Logo uploaded.");
    } catch (e) { setErr(formatError(e)); }
    finally { setBusy(false); }
  };

  const removeLogo = async () => {
    setBusy(true); setErr("");
    try {
      await api.delete("/admin/branding/logo");
      await refresh();
      flash("Logo removed.");
    } catch (e) { setErr(formatError(e)); }
    finally { setBusy(false); }
  };

  return (
    <AppShell>
      <div className="flex flex-col gap-6 max-w-3xl">
        <div>
          <div className="text-xs uppercase tracking-widest" style={{ color: "var(--text-dim)" }}>
            Back Office · Branding
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold" style={{ letterSpacing: "-0.04em" }}>
            Make Born Day your own.
          </h1>
          <p style={{ color: "var(--text-dim)" }} className="mt-2">
            Site-wide font, logo, and wordmark. Applies to every page for every visitor immediately.
          </p>
        </div>

        {err && (
          <div className="text-sm px-3 py-2 rounded" style={{ background: "color-mix(in srgb, #ff3b30 15%, transparent)", color: "#ff3b30" }} data-testid="admin-branding-error">
            {err}
          </div>
        )}
        {ok && <div className="bd-chip" data-testid="admin-branding-ok">{ok}</div>}

        {/* Font picker */}
        <div className="bd-surface p-5">
          <div className="flex items-center gap-2 mb-4 font-bold text-lg">
            <Type size={18} /> Global font
            <span className="text-xs" style={{ color: "var(--text-dim)" }}>
              · current: <code className="bd-mono">{branding?.font_key}</code>
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {FONT_OPTIONS.map((f) => {
              const active = branding?.font_key === f.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  className="bd-card flex flex-col items-center justify-center text-center transition"
                  style={{
                    padding: "0.85rem 0.5rem",
                    fontFamily: getFontPreviewStack(f.key),
                    background: active ? "var(--primary)" : undefined,
                    color: active ? "var(--primary-fg)" : undefined,
                    borderColor: active ? "var(--primary)" : undefined,
                    cursor: busy ? "not-allowed" : "pointer",
                  }}
                  disabled={busy}
                  onClick={() => saveFont(f.key)}
                  data-testid={`admin-font-${f.key}`}
                >
                  <span style={{ fontSize: "1.6rem", lineHeight: 1, fontWeight: 700 }}>{f.sample}</span>
                  <span className="text-[10px] mt-1 uppercase tracking-widest">{f.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Wordmark */}
        <div className="bd-surface p-5">
          <div className="flex items-center gap-2 mb-4 font-bold text-lg">
            <RefreshCw size={18} /> Wordmark
          </div>
          <div className="flex gap-2 mb-3">
            <input
              className="bd-input flex-1"
              value={wordmark}
              onChange={(e) => setWordmark(e.target.value)}
              maxLength={60}
              data-testid="admin-branding-wordmark"
              placeholder="Born Day"
            />
            <button className="bd-btn" onClick={saveWordmark} disabled={busy} data-testid="admin-branding-wordmark-save">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <><Save size={14} /> Save</>}
            </button>
          </div>
          <label className="text-sm flex items-center gap-2">
            <input
              type="checkbox"
              checked={showWordmark}
              onChange={(e) => { setShowWordmark(e.target.checked); }}
              data-testid="admin-branding-show-wordmark"
            />
            Show the wordmark text next to the logo
          </label>
        </div>

        {/* Front-page slogan editor */}
        <div className="bd-surface p-5">
          <div className="flex items-center gap-2 mb-1 font-bold text-lg">
            <RefreshCw size={18} /> Front-page slogan
          </div>
          <p className="text-xs mb-4" style={{ color: "var(--text-dim)" }}>
            Top of the dashboard. One click saves it for every visitor instantly.
            Live preview below shows exactly how it will look on the calendar page.
          </p>
          <div className="grid gap-3 mb-3">
            <div>
              <label className="bd-label">Eyebrow (small label above the slogan)</label>
              <input
                className="bd-input"
                value={tagline}
                onChange={(e) => setTagline(e.target.value)}
                maxLength={60}
                placeholder="Your calendar"
                data-testid="admin-branding-tagline"
              />
            </div>
            <div>
              <label className="bd-label">Slogan (the big sentence)</label>
              <input
                className="bd-input"
                value={slogan}
                onChange={(e) => setSlogan(e.target.value)}
                maxLength={200}
                placeholder="Every day. Every person. All in one place."
                data-testid="admin-branding-slogan"
              />
            </div>
          </div>
          {/* Live preview */}
          <div
            className="bd-card mb-3"
            style={{ padding: "1rem 1.25rem", background: "var(--bg-soft)" }}
            data-testid="admin-branding-slogan-preview"
          >
            <div className="text-xs uppercase tracking-widest" style={{ color: "var(--text-dim)" }}>
              {tagline || "Your calendar"}
            </div>
            <div className="text-2xl sm:text-3xl font-extrabold" style={{ letterSpacing: "-0.04em" }}>
              {slogan || "Every day. Every person. All in one place."}
            </div>
          </div>
          <button
            className="bd-btn"
            onClick={saveHero}
            disabled={busy}
            data-testid="admin-branding-slogan-save"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <><Save size={14} /> Save slogan</>}
          </button>
        </div>

        {/* Logo */}
        <div className="bd-surface p-5">
          <div className="flex items-center gap-2 mb-4 font-bold text-lg">
            <Upload size={18} /> Logo image
          </div>
          <div className="flex items-start gap-5 flex-wrap">
            <div
              className="w-24 h-24 rounded grid place-items-center"
              style={{ background: "var(--bg-soft)", border: "1px dashed var(--border)" }}
            >
              {logoSrc ? (
                <img src={logoSrc} alt="Logo" className="w-24 h-24 rounded object-cover" data-testid="admin-branding-logo-preview" />
              ) : (
                <span className="text-xs" style={{ color: "var(--text-dim)" }}>No logo</span>
              )}
            </div>
            <div className="flex-1 min-w-[200px] flex flex-col gap-3">
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(e) => uploadLogo(e.target.files?.[0])}
                data-testid="admin-branding-logo-file"
              />
              <div className="flex gap-2 flex-wrap">
                <button className="bd-btn" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="admin-branding-logo-upload">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <><Upload size={14} /> Upload logo</>}
                </button>
                {logoSrc && (
                  <button className="bd-btn bd-btn-ghost" onClick={removeLogo} disabled={busy} data-testid="admin-branding-logo-remove">
                    <X size={14} /> Remove
                  </button>
                )}
              </div>
              <p className="text-xs" style={{ color: "var(--text-dim)" }}>
                PNG / JPEG / WebP up to 4MB. Auto-cropped to a 256×256 square. Goes live for every visitor on save.
              </p>
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function getFontPreviewStack(key) {
  switch (key) {
    case "inter": return "'Inter', system-ui, sans-serif";
    case "playfair": return "'Playfair Display', Georgia, serif";
    case "space-grotesk": return "'Space Grotesk', system-ui, sans-serif";
    case "manrope": return "'Manrope', system-ui, sans-serif";
    case "dm-serif": return "'DM Serif Display', Georgia, serif";
    case "ibm-plex": return "'IBM Plex Sans', system-ui, sans-serif";
    case "jetbrains-mono": return "'JetBrains Mono', monospace";
    default: return "system-ui, sans-serif";
  }
}
